import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { QuizQuestionType } from '@edtech-ui/constants/quiz-question-type.enum';
import { FullQuizData } from '@edtech-ui/core/models/quiz-creation/ai-quiz-generator';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { getCorrectOptionIndex, isCorrectQuizOption } from '@edtech-ui/core/utils/assessment-quiz/assessment-quiz-answer.utils';
import { MathLatexComponent } from '@edtech-ui/shared/latex/math-latex/math-latex.component';
import { QuestionBlocksComponent } from '@edtech-ui/shared/question-blocks/question-blocks.component';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
@Component({
	selector: 'app-question-preview',
	standalone: true,
	imports: [CommonModule, FormsModule, MathLatexComponent, QuestionBlocksComponent],
	templateUrl: './question-preview.component.html',
	styleUrl: './question-preview.component.scss'
})

export class QuestionPreviewComponent implements OnInit, OnDestroy {

	@Input() isPreviewVisible: boolean = false;
	subscription = new Subscription();
	_quizData: FullQuizData | null = null;
	quizId!: string;
	QuizQuestionType = QuizQuestionType;
	isCorrectOption = isCorrectQuizOption;
	getCorrectOptionIndex = getCorrectOptionIndex;


	constructor(
		private _quizService: QuizManagementService,
		private _toastr: ToastrService,
		private route: ActivatedRoute,
	) { }


	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			this.quizId = params.get('quizId')!;
		});

		this.loadQuizFromLocalStorage();
		this.getQuizData();

		if (this.quizId) {
			this.getQuizDetailsById();
		}
	}
	loadQuizFromLocalStorage(): void {

		const userId = localStorage.getItem('user_id');

		if (!userId) return;

		const storageQuizDataKey = `${userId}_ai_quiz_data`;
		const quizDataStored = localStorage.getItem(storageQuizDataKey);

		if (!quizDataStored) return;

		try {

			const parsed = JSON.parse(quizDataStored);
			if (parsed?.questions?.length) {
				this._quizData = parsed;
				this.isPreviewVisible = true;
			}
		} catch (error) {
			this._toastr.error('Failed to load saved quiz preview');
		}
	}


	getQuizData(): void {
		this.subscription.add(
			this._quizService.saveQuizData.subscribe(data => {
				if (data?.questions?.length) {
					this._quizData = { ...this._quizData, ...data };
					this.isPreviewVisible = true;
					const userId = localStorage.getItem('user_id');

					if (userId) {
						localStorage.setItem(`${userId}_ai_quiz_data`, JSON.stringify(data));
					}
				}
			})
		);
	}


	getQuizDetailsById(): void {

		if (!this.quizId) return;

		this.subscription.add(
			this._quizService.getQuizById(this.quizId).subscribe({
				next: (response) => {
					if (response.Success && response.Data) {
						this._quizData = response.Data;
						this.isPreviewVisible = true;

						const userId = localStorage.getItem('user_id');
						if (userId) {
							localStorage.setItem(`${userId}_ai_quiz_data`, JSON.stringify(response.Data));
						}
					} else {
						this._toastr.error(response.Message);
					}
				},
				error: () => {
					this._toastr.error('Failed to fetch quiz details');
				}
			})
		);
	}


	getOptionLabel(index: number): string {
		const labels = ['A', 'B', 'C', 'D'];
		return labels[index] || '';
	}

	hasBadges(q: any): boolean {
		return !!(
			q?.difficulty_level ||
			q?.topic ||
			q?.sub_topics ||
			this._quizData?.quiz_name
		);
	}

	hasImageBlock(question: any): boolean {
		return question?.question_blocks?.some(
			(block: any) => block.type === 'image'
		) ?? false;
	}

	shouldRenderLegacyDiagram(question: any): boolean {
		const hasImageBlock =
			question?.question_blocks?.some(
				(b: any) => b.type === 'image'
			) ?? false;

		const hasDiagrams =
			question?.diagrams?.length > 0;

		const hasDiagramPlaceholder =
			question?.question_text?.includes('[DIAGRAM_');

		return !hasImageBlock &&
			!hasDiagrams &&
			!hasDiagramPlaceholder;
	}

	removeDiagramTag(text: string | null | undefined): string {
		if (!text) return '';
		return text.replace(/\[DIAGRAM\]/gi, '').trim();
	}



	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}